import { Injectable, Renderer2, RendererFactory2 } from "@angular/core";
import { AuthHelperService } from "app/auth/helpers/auth-helper.service";
import { Field } from "app/interfaces/fields";
import { AuthFacade } from "app/store/auth.facade";
import { firstValueFrom, take } from "rxjs";
import { generateCalculation } from "../calculation";
import { applyRelations } from "../fieldrelation";
import { onFieldValidation } from "../fieldvalidation";
import { GlobalService } from "../global.service";
import { Deal, DealData } from "./integration.dto";
import { PipeDriveService } from "./pipedrive/pipe-drive.service";
import { HttpClient } from "@angular/common/http";
import { FormArray, FormGroup } from "@angular/forms";
import { updateFieldValue } from "../synchronize";
import { CrmData, Payload } from "app/store/reducers/auth.reducer";
import { Recipient, RecipientRole, TemplateForm } from "app/main/pages/edit-template/edit-template.dto";
import { SelectTemplateService } from "app/main/pages/general-modals/select-template/select-template.service";
import { HelperService } from "app/main/pages/edit-template/helper.service";

@Injectable({
  providedIn: "root",
})
export class IntegrationService {
  private _isThirdPartyLogin$ = this._authFacade$.isThirdPartyLogin$;
  private renderer: Renderer2;
  public dealData: Deal;
  constructor(
    private _authFacade$: AuthFacade,
    private _authHelper: AuthHelperService,
    private pipeDriveService: PipeDriveService,
    private globalService: GlobalService,
    private rendererFactory: RendererFactory2,
    private _http: HttpClient,
    private selectTemplateService: SelectTemplateService,
    private helperService: HelperService,
  ) {
    this.renderer = rendererFactory.createRenderer(null, null);
  }

  async setCrmLayout() {
    const isThirdPartyLogin = await this.isCrmLoggedIn();
    if (isThirdPartyLogin) {
      this._authHelper.setLayoutConfig(true, false, false);
    }
    return isThirdPartyLogin;
  }

  async isCrmLoggedIn() {
    const isThirdPartyLogin: boolean = await firstValueFrom(
      this._isThirdPartyLogin$.pipe(take(1))
    );
    return isThirdPartyLogin;
  }



  /**
   * Add Persons as role
   * @param dealData 
   * @param template 
   * @param template_id 
   */
  addPersonsAndRoles(
    dealData: DealData,
    template: TemplateForm,
    template_id: number | null
  ) {
    this.selectTemplateService.loadingSubject.next(true);
    this.pipeDriveService.getDealDetail(dealData).subscribe({
      next: async (recipients: Deal) => {
        const recipientsData = recipients?.data?.contacts_data ?? [];
        const index = 0;
        template.template_roles.forEach((template_role) => {
          if (
            !template_role.recipient_id &&
            recipientsData?.length &&
            recipientsData[index] != undefined
          ) {
            const person: Recipient = recipientsData[index];
            if (person) {
              const populatePerson = new FormArray(
                this.helperService.getPersonfields(
                  template_role.personfields,
                  person,
                  true,
                  true
                )
              );
              const populateOrg = new FormArray(
                this.helperService.getOrgfields(
                  template_role.orgfields,
                  person.organization,
                  true,
                  true
                )
              );
              Object.assign(template_role, {
                recipient_id: person?.id ?? null,
                recipient: person,
                organization_id: person?.organization_id ?? null,
                organization: person.organization,
                personfields: populatePerson.value,
                orgfields: populateOrg.value,
              });
              recipientsData.splice(index, 1);
              index + 1;
            }
          }
        });
        for (const person of recipientsData) {
          const role = this.helperService.addNewRole(
            "id",
            null,
            template.template_roles.length + 1,
            true,
            person
          );
          template.template_roles.push(role);
        }
        this.selectTemplateService.goToSendDocument(template, template_id);
      },
    });
  }


  /**
   * Generate Recipient Roles 
   * @param recipientsData 
   * @returns 
   */
  generateRecipientRoles(recipientsData) {
    const recipientRoles: RecipientRole[] = [];
    for (const person of recipientsData) {
      const role = this.helperService.addNewRole(
        "id",
        null,
        recipientRoles.length + 1,
        true,
        person
      );
      recipientRoles.push(role as unknown as RecipientRole);
    }
    return recipientRoles;
  }



  /**
   * Filling tags and fields
   * @param templateForm 
   */
  async fillInTheTagsAndFields(templateForm: FormGroup) {
    if (await this.isCrmLoggedIn()) {
      this._authFacade$.crmData$.pipe(take(1)).subscribe({
        next: (crmData: CrmData) => {
          const dealData = this.getCrmParams(crmData);
          this.pipeDriveService
            .getDealDetail(dealData)
            .pipe(take(1))
            .subscribe({
              next: (deal: Deal) => {
                this.dealData = deal;
                this.processFillingTags();
                this.processFillingCustomFields(templateForm);
              },
              error: (err) => {
                this.globalService.toastrWarning(err);
              },
            });
        },
      });
    }
  }

  getCrmParams(crmData: CrmData) {
    const payload: Payload = JSON.parse(crmData.payload);
    if (crmData?.provider === "msdynamic") {
      return {
        deal_id: payload.id,
        provider: crmData.provider,
        entity_name: payload.entity_name,
        type: payload.type
      };
    }
    return { deal_id: payload.id, provider: crmData.provider, type: payload.type };
  }

  processFillingTags() {
    for (const key in this.globalService.fields_data) {
      if (this.globalService.fields_data[key]) {
        const field: Field = this.globalService.fields_data[key];
        this.fillingTag(field);
      }
    }
  }

  async fillingTag(field: Field) {
    if (!(await this.isCrmLoggedIn())) return;
    if (
      field.field_info == "document_field" &&
      (field.selected_for_role_id == 0 ||
        field.selected_for_role_id == -1) &&
      field.type != "signature"
    ) {
      const detailValue = this.dealData.data.deal_data[field.api_attribute];

      if (detailValue) {
        switch (field.type) {
          case "text":
            this.handleTextField(field, detailValue);
            break;
          case "datepicker":
            this.handleDatepickerField(field, detailValue);
            break;
          case "droplist":
          case "list":
            this.handleDroplistField(field, detailValue);
            break;
          // Add more cases as needed
          default:
            console.warn(`Unhandled field type: ${field.type}`);
        }
      }
    }
  }

  // Handling functions for each text field
  handleTextField(field: Field, value: string) {
    const selector = document.querySelector(
      '.drag-fields[uuid="' + field.uuid + '"]'
    );
    if (selector) {
      const inputSelector = selector.querySelector(
        ".text-tag-editor, .text-field-input, .text-field-textarea"
      );
      if (field.type_of == "tag") {
        const tagView = selector.querySelector("span.text-tag");
        this.renderer.setProperty(
          tagView,
          "textContent",
          value == "" ? this.globalService.generateTagText(field.uuid) : value
        );
      }
      this.renderer.setAttribute(inputSelector, "value", value);
      this.renderer.setProperty(inputSelector, "value", value);
      if (field.allow_multi_lines) {
        this.renderer.setProperty(inputSelector, "textContent", value);
      }
      this.globalService.updateField(
        field.uuid,
        { value: value },
        (updatedField: Field) => {
          // this.emitter.patchData({ uuid: field.uuid, value: value });
          onFieldValidation(
            updatedField,
            this.globalService,
            updatedField.selected_for_role_id
          );
          if (inputSelector.classList.contains("calculation")) {
            generateCalculation(field.uuid, this.globalService, true);
          }
          applyRelations(field.uuid, this.globalService);
        }
      );
    }
  }

  // Handling functions for each datepicker field
  handleDatepickerField(field: Field, value: string) {
    const selector = document.querySelector(
      '.drag-fields[uuid="' + field.uuid + '"]'
    );
    if (selector) {
      if (field.type_of == "tag") {
        const tagView = selector.querySelector("span.tag-date");
        this.renderer.setProperty(
          tagView,
          "textContent",
          value == "" ? this.globalService.generateTagText(field.uuid) : value
        );
      } else {
        const inputSelector = selector.querySelector(".datepicker-field");
        if (inputSelector["_flatpickr"]) {
          inputSelector["_flatpickr"].setDate(value);
        }
        this.renderer.setAttribute(inputSelector, "value", value);
        this.renderer.setProperty(inputSelector, "value", value);
      }
      this.globalService.updateField(field.uuid, { value: value }, () => {
        applyRelations(field.uuid, this.globalService);
      });
    }
  }

  // Handling functions for each droplist|list field
  handleDroplistField(field: Field, value: string) {
    const selector = document.querySelector(
      '.drag-fields[uuid="' + field.uuid + '"]'
    );
    if (selector) {
      if (field.type == "droplist" && !value.includes(",")) {
        const selectedOption = field.options.find(
          (option) => value == option.crm_option_id
        );
        const selectedOptionLabel = selector.querySelector(
          ".selected-option-label"
        );
        this.globalService.updateField(
          field.uuid,
          { value: selectedOption.value },
          () => {
            this.renderer.setProperty(
              selectedOptionLabel,
              "textContent",
              selectedOption.value
            );
            applyRelations(field.uuid, this.globalService);
          }
        );
        // this.emitter.patchData({ uuid: field.uuid, value: selectedOption});
      } else if (field.type == "list") {
        if (field.multi_choice == "0") {
          const selectedOption = field.options.find(
            (option) => value == option.crm_option_id
          );
          if (selectedOption) {
            const multiChoiceItem = selector.querySelector(
              '.multiChoice-item[option_uuid="' + selectedOption.id + '"]'
            ) as HTMLElement;
            this.renderer.addClass(multiChoiceItem, "multiChoice-active");
            this.globalService.updateField(field.uuid, {
              value: selectedOption.value,
            });
            applyRelations(field.uuid, this.globalService);
          }
        } else if (field.multi_choice == "1") {
          const ids = value.split(",");
          for (const id of ids) {
            const selectedOption = field.options.find(
              (option) => id == option.crm_option_id
            );
            const multiChoiceItem = selector.querySelector(
              '.multiChoice-item[option_uuid="' + selectedOption.id + '"]'
            ) as HTMLElement;
            this.renderer.addClass(multiChoiceItem, "multiChoice-active");
          }
          const {values} = this.globalService.getOptionValues(field.uuid);
          this.globalService.updateField(field.uuid, {
            value: values,
          });
          applyRelations(field.uuid, this.globalService);
        }
      }
    }
  }

  /**
   * Process filling the custom fields: person | org
   * @param {FormGroup} templateForm - The form group containing the template roles
   */
  processFillingCustomFields(templateForm: FormGroup) {
    (templateForm.get("template_roles") as FormArray).controls.forEach(
      (form: FormGroup) => {
        // Find the recipient matching the recipient_id in the form
        const recipient = this.dealData.data.contacts_data.find(
          (recipient: Recipient) => recipient.id === form.value.recipient_id
        );

        if (recipient) {
          // Get the personfields form array
          const personFields = form.get("personfields") as FormArray;

          // Iterate over each person field form group
          personFields.controls.forEach((personField: FormGroup) => {
            // Check if the recipient object has a property matching the name of the person field
            const personFieldName = personField.get("name").value;
            if (recipient?.customFields?.personFieldName) {
              const value = recipient.customFields[personFieldName];
              personField.get("value").setValue(value);
              personField
                .get("key")
                .setValue(
                  personFieldName.replace(
                    /[^A-Za-zÀ-ÖØ-öø-ÿĄąĆćĘęŁłŃńÓóŚśŹźŻż0-9.,?!@#&()\-'"%;:^$]/g,
                    "_"
                  )
                );
              updateFieldValue(
                form.value.recipient_id,
                personField,
                this.globalService,
                "person_field"
              );
            }
          });
        }
      }
    );
  }

  /**
   * Fetch deal data
   * @param dealId
   * @returns
   */
  fetchDealData(dealId: string) {
    return this._http.get(`integrations/pipedrive/deals/${dealId}/viewtoken`);
  }
}
